import React, { useState, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Canvas } from '@react-three/fiber';
import { motion, useScroll } from 'framer-motion';
import { ArrowRight, Check, Menu, X } from 'lucide-react';
import { RotatingHeroText } from '../common/RotatingHeroText';
import { CanvasErrorBoundary } from '../landing-3d/CanvasFallback';
import { Scene3DTherapist } from '../landing-3d/Scene3DTherapist';
import { trackEvent } from '../../seo/analytics';
import './LandingTherapist.css';

const TherapistCanvasFallback = () => (
  <div className="landing-therapist-3d-fallback" aria-hidden />
);

const HERO_ROTATING_HEADLINES = [
  'Extend Therapy Impact Beyond the Session.',
  'Automate SOAP Notes in Seconds.',
  'Track Progress Across Therapy, Home, and School.',
  'Analyze IEP Goals and Predict the Next Milestone.',
  'Generate Clinical-Grade Reports Instantly.',
];

const SIGNUP_DEFAULT_ROLE_KEY = 'signupDefaultRole';
/** Set when user opens sign-up from /therapists; must match ProviderVerification SIGNUP_FROM_THERAPISTS_LINK_KEY. */
const SIGNUP_FROM_THERAPISTS_LINK_KEY = 'signupFromTherapistsLink';

/* Blue / purple / teal capability tiles */
const HERO_FEATURE_NODES = [
  { label: 'AI Milestone Prediction', color: '#0ea5e9' },
  { label: 'IEP Analysis', color: '#7c3aed' },
  { label: 'Goal Tracking', color: '#0ea5e9' },
  { label: 'Clinical-Grade Reports', color: '#0d9488' },
  { label: 'AI SOAP Notes', color: '#7c3aed' },
  { label: 'Parent Feedback Loop', color: '#0ea5e9' },
  { label: 'Social Stories Library', color: '#7c3aed' },
  { label: 'AI Comprehension', color: '#0ea5e9' },
];

function LandingTherapist() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { scrollYProgress } = useScroll();

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  /** Open the shared Auth modal in sign-up mode with role THERAPIST (same flow as login page, default role therapist). */
  const openSignUpAsTherapist = () => {
    trackEvent('therapist_marketplace_cta', { surface: 'therapist_landing' });
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(SIGNUP_DEFAULT_ROLE_KEY, 'THERAPIST');
      localStorage.setItem(SIGNUP_FROM_THERAPISTS_LINK_KEY, 'true');
    }
    navigate('/?openAuth=signUp');
  };

  return (
    <div className="landing-therapist-page">
      {/* Full-page 3D background – fixed behind all content */}
      <div className="landing-therapist-3d-bg" aria-hidden>
        <CanvasErrorBoundary fallback={<TherapistCanvasFallback />}>
          <Canvas
            camera={{ position: [0, 0, 3], fov: 50 }}
            dpr={[1, 1.5]}
            gl={{ antialias: true, alpha: true }}
            style={{ display: 'block', width: '100%', height: '100%' }}
          >
            <Suspense fallback={null}>
              <Scene3DTherapist />
            </Suspense>
          </Canvas>
        </CanvasErrorBoundary>
      </div>

      {/* Scroll progress bar */}
      <motion.div
        className="landing-therapist-scroll-progress"
        style={{ scaleX: scrollYProgress }}
      />

      <div className="landing-therapist-content">
      {/* Header */}
      <header className="bg-slate-900/95 border-b border-slate-700 sticky top-0 z-50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex-1 min-w-0" aria-hidden />
            <nav className="hidden md:flex items-center justify-center flex-1 flex-nowrap gap-6 lg:gap-8 min-w-0">
              <button type="button" onClick={() => scrollToSection('problem')} className="text-slate-300 hover:text-white transition-colors font-medium whitespace-nowrap shrink-0 text-sm lg:text-base">
                Why Progress Breaks Down
              </button>
              <button type="button" onClick={() => scrollToSection('solution')} className="text-slate-300 hover:text-white transition-colors font-medium whitespace-nowrap shrink-0 text-sm lg:text-base">
                How TinyWins Solves This
              </button>
              <button type="button" onClick={() => scrollToSection('features')} className="text-slate-300 hover:text-white transition-colors font-medium whitespace-nowrap shrink-0 text-sm lg:text-base">
                Features
              </button>
            </nav>
            <div className="flex-1 flex justify-end min-w-0" aria-hidden>
            <button
              type="button"
              className="md:hidden p-2 text-slate-300 hover:text-white"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            </div>
          </div>
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-700">
              <div className="flex flex-col space-y-4">
                <button type="button" onClick={() => scrollToSection('problem')} className="text-left text-slate-300 hover:text-white font-medium">
                  Why Progress Breaks Down
                </button>
                <button type="button" onClick={() => scrollToSection('solution')} className="text-left text-slate-300 hover:text-white font-medium">
                  How TinyWins Solves This
                </button>
                <button type="button" onClick={() => scrollToSection('features')} className="text-left text-slate-300 hover:text-white font-medium">
                  Features
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Hero */}
      <section className="pt-6 pb-24 lg:pt-10 lg:pb-32 landing-therapist-hero-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg sm:text-xl font-semibold text-slate-200 mb-5 max-w-2xl mx-auto landing-therapist-subhero-tagline">
            Built for independent therapists and growing clinics.
          </p>
          <div className="max-w-2xl mx-auto mb-5 min-h-[4.5rem] sm:min-h-[4rem] lg:min-h-[3.5rem]">
            <RotatingHeroText
              lines={HERO_ROTATING_HEADLINES}
              intervalMs={3750}
              className="w-full"
              lineClassName="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight text-center landing-therapist-headline"
              as="h1"
            />
          </div>
          <p className="text-lg sm:text-xl text-slate-300 mb-6 max-w-2xl mx-auto font-medium">
            Structured tools for progress tracking, parent collaboration, and smarter therapy workflows.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <button
              type="button"
              onClick={openSignUpAsTherapist}
              className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-500 transition-colors shadow-lg hover:shadow-xl"
            >
              Start Free Trial
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => scrollToSection('book-demo')}
              className="inline-flex items-center justify-center border-2 border-slate-500 text-slate-200 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-slate-700/50 transition-colors"
            >
              Book a Demo
            </button>
          </div>
          <p className="text-sm text-slate-400 mb-5 font-medium">30-day free trial. Cancel anytime.</p>
          <p className="text-sm text-slate-300 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 font-medium">
            <span className="inline-flex items-center"><Check className="h-4 w-4 text-emerald-400 mr-1.5" aria-hidden /> Works with your existing EMR</span>
            <span className="inline-flex items-center"><Check className="h-4 w-4 text-emerald-400 mr-1.5" aria-hidden /> HIPAA compliant</span>
            <span className="inline-flex items-center"><Check className="h-4 w-4 text-emerald-400 mr-1.5" aria-hidden /> Built for ABA, SLP & OT</span>
          </p>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-14 lg:py-18 landing-therapist-hero-bg border-t border-slate-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">
            Features
          </h2>
          <div className="landing-therapist-hero-tiles">
            {HERO_FEATURE_NODES.map((node) => (
              <div
                key={node.label}
                className="landing-therapist-hero-tile"
                style={{ background: node.color }}
              >
                <span className="landing-therapist-hero-tile-text">{node.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Problem */}
      <section id="problem" className="py-14 lg:py-18 bg-slate-900 border-t border-slate-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-10 text-center">
            Why Progress Breaks Down Outside Sessions
          </h2>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="rounded-xl border border-slate-600 bg-slate-800/80 p-5 shadow-sm hover:shadow-md transition-shadow min-h-[120px] flex flex-col">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-sky-900/80 text-sky-300 font-semibold text-sm mb-3 shrink-0">1</span>
              <p className="font-semibold text-white mb-1">Therapy is 1–2 hours a week.</p>
              <p className="text-slate-300 text-sm">The other 160+ hours determine outcomes.</p>
            </div>
            <div className="rounded-xl border border-slate-600 bg-slate-800/80 p-5 shadow-sm hover:shadow-md transition-shadow min-h-[120px] flex flex-col">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-sky-900/80 text-sky-300 font-semibold text-sm mb-3 shrink-0">2</span>
              <p className="font-semibold text-white mb-1">Parents aren&apos;t consistently reinforcing goals at home.</p>
              <p className="text-slate-300 text-sm">Without structure, skills don&apos;t always carry over.</p>
            </div>
            <div className="rounded-xl border border-slate-600 bg-slate-800/80 p-5 shadow-sm hover:shadow-md transition-shadow min-h-[120px] flex flex-col">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-sky-900/80 text-sky-300 font-semibold text-sm mb-3 shrink-0">3</span>
              <p className="font-semibold text-white mb-1">The same goals repeat for months.</p>
              <p className="text-slate-300 text-sm">Progress inside sessions doesn&apos;t always show up in real life.</p>
            </div>
            <div className="rounded-xl border border-slate-600 bg-slate-800/80 p-5 shadow-sm hover:shadow-md transition-shadow min-h-[120px] flex flex-col">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-sky-900/80 text-sky-300 font-semibold text-sm mb-3 shrink-0">4</span>
              <p className="font-semibold text-white">Inconsistent carryover impacts parent confidence — and long-term retention.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Solution */}
      <section id="solution" className="py-14 lg:py-18 bg-slate-800/50 border-t border-slate-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">
            How TinyWins Solves This
          </h2>
          <ul className="space-y-5">
            <li className="flex gap-3">
              <Check className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
              <p className="text-slate-200">Structured milestone tracking aligned to therapy goals.</p>
            </li>
            <li className="flex gap-3">
              <Check className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
              <p className="text-slate-200">Clear visibility for parents between sessions.</p>
            </li>
            <li className="flex gap-3">
              <Check className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
              <p className="text-slate-200">Guided reinforcement that extends beyond the clinic.</p>
            </li>
            <li className="flex gap-3">
              <Check className="h-5 w-5 text-emerald-400 shrink-0 mt-0.5" aria-hidden />
              <p className="text-slate-200">Documented, shareable progress that builds confidence.</p>
            </li>
          </ul>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-14 lg:py-18 bg-slate-900 border-t border-slate-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">
            Who It&apos;s For
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-slate-600 bg-slate-800/80 p-6">
              <h3 className="font-semibold text-white mb-2">For Independent Therapists</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Differentiate your practice with structured milestone tracking that makes progress visible between sessions.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-600 bg-slate-800/80 p-6">
              <h3 className="font-semibold text-white mb-2">For Growing Clinics</h3>
              <p className="text-slate-300 text-sm leading-relaxed">
                Improve carryover, increase parent confidence, and strengthen long-term retention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Get started */}
      <section id="get-started" className="py-14 lg:py-18 bg-slate-800/50 border-t border-slate-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Ready for therapists &amp; clinics
          </h2>
          <p className="text-slate-300 font-medium mb-8">
            Start with a free trial, or contact us for clinic and organization options. Plans are tailored — we do not publish a fixed price list.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={openSignUpAsTherapist}
              className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-3 rounded-lg text-base font-semibold hover:bg-blue-500 transition-colors"
            >
              Start free trial
            </button>
            <a
              href="mailto:sales@tinywins.ai"
              className="inline-flex items-center justify-center border-2 border-slate-500 text-slate-200 px-8 py-3 rounded-lg text-base font-semibold hover:bg-slate-700/50 transition-colors"
            >
              Contact sales@tinywins.ai
            </a>
          </div>
        </div>
      </section>

      {/* Differentiation */}
      <section className="py-14 lg:py-18 bg-slate-900 border-t border-slate-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Not Just Another AI Tool
          </h2>
          <p className="text-slate-300 mb-4">
            Not a chatbot. Not generic therapy software. Not a parent-only app.
          </p>
          <p className="text-slate-200 font-medium">
            TinyWins is a structured continuity system designed to make therapy progress stick.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 lg:py-20 landing-therapist-hero-bg border-t border-slate-700">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Make Therapy Progress Carry Over.
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Whether you&apos;re an independent therapist or a growing clinic, TinyWins helps your work extend beyond the session.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={openSignUpAsTherapist}
              className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-500 transition-colors shadow-lg"
            >
              Join the Therapist Marketplace
              <ArrowRight className="ml-2 h-5 w-5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => scrollToSection('get-started')}
              className="inline-flex items-center justify-center border-2 border-slate-500 text-slate-200 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-slate-700/50 transition-colors"
            >
              Talk to us about plans
            </button>
          </div>
        </div>
      </section>

      {/* Book a Demo – Calendly */}
      <section id="book-demo" className="py-14 lg:py-18 bg-slate-900 border-t border-slate-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2 text-center">
            Book a Demo
          </h2>
          <p className="text-slate-300 text-center mb-8">
            Schedule a call to see how TinyWins can work for you.
          </p>
          <div className="rounded-xl overflow-hidden bg-slate-800/50 border border-slate-600">
            <iframe
              title="Book a demo with TinyWins"
              src={`https://calendly.com/synchronicityaiorg/30min?embed_domain=${typeof window !== 'undefined' ? window.location.hostname : 'tinywins.ai'}&embed_type=Inline`}
              width="100%"
              height="700"
              style={{ minWidth: '320px', border: 'none' }}
            />
          </div>
        </div>
      </section>
      </div>
    </div>
  );
}

export default LandingTherapist;
